import { X, Book, Plane, Globe2, Users, User as UserIcon, Home } from 'lucide-react';
import { useState } from 'react';

interface HelpProps {
  onClose: () => void;
}

type GuideSection = 'home' | 'getting-started' | 'adding-flights' | 'using-globe' | 'managing-friends' | 'profile-settings';

export function Help({ onClose }: HelpProps) {
  const [activeSection, setActiveSection] = useState<GuideSection>('home');

  const renderHome = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold gradient-text mb-4">User Guides</h2>
        <p className="text-white/70 mb-6">
          Welcome to Personal Flight Tracker! Select a guide below to learn how to use the platform.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={() => setActiveSection('getting-started')}
          className="glass rounded-xl p-6 text-left hover:bg-white/10 transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary-500/20 group-hover:bg-primary-500/30 transition-all">
              <Home className="w-6 h-6 text-primary-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Getting Started</h3>
              <p className="text-sm text-white/60">
                Create your account and learn the basics of the platform
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setActiveSection('adding-flights')}
          className="glass rounded-xl p-6 text-left hover:bg-white/10 transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary-500/20 group-hover:bg-primary-500/30 transition-all">
              <Plane className="w-6 h-6 text-primary-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Adding Flights</h3>
              <p className="text-sm text-white/60">
                Learn how to add one-way and round-trip flights to your tracker
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setActiveSection('using-globe')}
          className="glass rounded-xl p-6 text-left hover:bg-white/10 transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary-500/20 group-hover:bg-primary-500/30 transition-all">
              <Globe2 className="w-6 h-6 text-primary-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Using the Globe</h3>
              <p className="text-sm text-white/60">
                Master the 3D globe controls and navigation
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setActiveSection('managing-friends')}
          className="glass rounded-xl p-6 text-left hover:bg-white/10 transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary-500/20 group-hover:bg-primary-500/30 transition-all">
              <Users className="w-6 h-6 text-primary-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Managing Friends</h3>
              <p className="text-sm text-white/60">
                Connect with friends and compare your travel journeys
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setActiveSection('profile-settings')}
          className="glass rounded-xl p-6 text-left hover:bg-white/10 transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary-500/20 group-hover:bg-primary-500/30 transition-all">
              <UserIcon className="w-6 h-6 text-primary-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Profile Settings</h3>
              <p className="text-sm text-white/60">
                Customize your profile and manage account settings
              </p>
            </div>
          </div>
        </button>
      </div>

      <div className="glass rounded-xl p-6 mt-8">
        <h3 className="text-lg font-semibold mb-3">Quick Tips</h3>
        <ul className="space-y-2 text-sm text-white/70">
          <li>💡 On mobile, tap the menu icon to access flight controls</li>
          <li>💡 Use the Auto Rotate button to showcase your globe</li>
          <li>💡 Add all your historical flights to build a complete travel history</li>
          <li>💡 Compare stats with friends to motivate more travel!</li>
        </ul>
      </div>
    </div>
  );

  const renderGettingStarted = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold gradient-text">Getting Started</h2>
      
      <section>
        <h3 className="text-xl font-semibold mb-3">What is Personal Flight Tracker?</h3>
        <p className="text-white/70 mb-4">
          Personal Flight Tracker is a beautiful, interactive application that allows you to:
        </p>
        <ul className="space-y-2 text-white/70 ml-4">
          <li>📍 Track all your flights on a 3D globe</li>
          <li>✈️ Add flight details including dates and routes</li>
          <li>👥 Connect with friends and compare your travel journeys</li>
          <li>📊 View statistics about your travels</li>
          <li>🌍 Explore your flight paths on an interactive globe</li>
        </ul>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Creating Your Account</h3>
        <div className="glass rounded-lg p-4 mb-4">
          <h4 className="font-semibold mb-2">Google Sign-In (Recommended)</h4>
          <ol className="space-y-2 text-white/70 ml-4 list-decimal">
            <li>Click the "Sign in with Google" button</li>
            <li>Select your Google account</li>
            <li>Grant permissions to access basic profile information</li>
            <li>You're ready to go!</li>
          </ol>
        </div>
        <div className="glass rounded-lg p-4">
          <h4 className="font-semibold mb-2">Email Registration</h4>
          <ol className="space-y-2 text-white/70 ml-4 list-decimal">
            <li>Click "Sign Up" on the login page</li>
            <li>Enter your email address and create a password</li>
            <li>Check your email for a verification code</li>
            <li>Enter the verification code to activate your account</li>
          </ol>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">First Time Login</h3>
        <p className="text-white/70 mb-3">Once you're logged in, you'll see:</p>
        <ul className="space-y-2 text-white/70 ml-4">
          <li><strong>3D Globe</strong> - The main interactive globe showing the world</li>
          <li><strong>Stats Panel</strong> - Your travel statistics (top right)</li>
          <li><strong>Add Flight Form</strong> - Quick form to add flights (left sidebar)</li>
          <li><strong>Flight List</strong> - All your flights (below the add form)</li>
          <li><strong>Navigation Header</strong> - Profile, Friends, and Sign Out buttons</li>
        </ul>
      </section>

      <section className="glass rounded-lg p-4 bg-primary-500/10">
        <h3 className="text-lg font-semibold mb-2">Next Steps</h3>
        <p className="text-white/70 mb-3">Ready to start tracking your flights?</p>
        <button
          onClick={() => setActiveSection('adding-flights')}
          className="btn-primary"
        >
          Learn How to Add Flights →
        </button>
      </section>
    </div>
  );

  const renderAddingFlights = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold gradient-text">Adding Flights</h2>

      <section>
        <h3 className="text-xl font-semibold mb-3">Adding a One-Way Flight</h3>
        <ol className="space-y-3 text-white/70 ml-4 list-decimal">
          <li>
            <strong className="text-white">Open the Add Flight Form</strong>
            <p className="text-sm mt-1">Look for the "Add Flight" panel on the left sidebar. On mobile, tap the menu icon.</p>
          </li>
          <li>
            <strong className="text-white">Select Departure Airport</strong>
            <p className="text-sm mt-1">Click the "From" field and start typing the airport name, city, or IATA code (e.g., "London", "LHR").</p>
          </li>
          <li>
            <strong className="text-white">Select Arrival Airport</strong>
            <p className="text-sm mt-1">Click the "To" field and search for your destination airport.</p>
          </li>
          <li>
            <strong className="text-white">Choose Flight Date (Optional)</strong>
            <p className="text-sm mt-1">Select the date you took the flight to keep track of your travel timeline.</p>
          </li>
          <li>
            <strong className="text-white">Add the Flight</strong>
            <p className="text-sm mt-1">Click "Add Flight" and watch it appear on the globe as an arc!</p>
          </li>
        </ol>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Adding a Round-Trip Flight</h3>
        <div className="glass rounded-lg p-4">
          <p className="text-white/70 mb-3">Save time by adding both directions at once:</p>
          <ol className="space-y-2 text-white/70 ml-4 list-decimal">
            <li>Follow the steps above for the outbound flight</li>
            <li>Check the "Round Trip" checkbox</li>
            <li>Select the return date (optional)</li>
            <li>Click "Add Flight" to add both flights at once</li>
          </ol>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Airport Search Tips</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Search by City</strong>
            <p className="text-sm text-white/60 mt-1">Type "New York" to see all NYC airports</p>
          </div>
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Search by Code</strong>
            <p className="text-sm text-white/60 mt-1">Type "JFK", "LAX", "LHR" for quick selection</p>
          </div>
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Search by Name</strong>
            <p className="text-sm text-white/60 mt-1">Type "Heathrow" or "O'Hare"</p>
          </div>
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Multiple Airports</strong>
            <p className="text-sm text-white/60 mt-1">Some cities have multiple airports - choose carefully!</p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Managing Your Flights</h3>
        <div className="glass rounded-lg p-4 space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Editing Flights</h4>
            <p className="text-white/70 mb-2">Click the edit/pencil icon on any flight in your list to modify it.</p>
            <ul className="text-sm text-white/60 ml-4 list-disc space-y-1">
              <li>Update flight dates</li>
              <li>Change flight type (one-way/round-trip)</li>
              <li>Add or edit notes/descriptions for your flights</li>
              <li>Switch departure and arrival airports</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-2">Deleting Flights</h4>
            <p className="text-white/70">Find the flight in your flight list and click the trash/delete icon to remove it.</p>
          </div>
        </div>
      </section>

      <section className="glass rounded-lg p-4 bg-primary-500/10">
        <h3 className="text-lg font-semibold mb-2">💡 Pro Tips</h3>
        <ul className="space-y-2 text-white/70 text-sm">
          <li>✅ Add all your historical flights to build your complete travel history</li>
          <li>✅ Use round-trip for return journeys to save time</li>
          <li>✅ For layovers, add each leg separately</li>
          <li>✅ The form clears after submission for quick bulk adding</li>
        </ul>
      </section>
    </div>
  );

  const renderUsingGlobe = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold gradient-text">Using the Globe</h2>

      <section>
        <h3 className="text-xl font-semibold mb-3">Desktop Controls</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500 flex items-center gap-2 mb-2">
              🖱️ Rotating
            </strong>
            <p className="text-white/70 text-sm">Click and drag anywhere on the globe to rotate it in any direction.</p>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500 flex items-center gap-2 mb-2">
              🔍 Zooming
            </strong>
            <p className="text-white/70 text-sm">Scroll up to zoom in, scroll down to zoom out. Get close or see the whole Earth!</p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Mobile Controls</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500 flex items-center gap-2 mb-2">
              👆 Rotating
            </strong>
            <p className="text-white/70 text-sm">Touch and drag with one finger to rotate the globe.</p>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500 flex items-center gap-2 mb-2">
              🤏 Zooming
            </strong>
            <p className="text-white/70 text-sm">Pinch outward to zoom in, pinch inward to zoom out.</p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Auto-Rotate Feature</h3>
        <div className="glass rounded-lg p-4">
          <p className="text-white/70 mb-3">The Auto-Rotate button at the bottom of the screen controls automatic globe rotation.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <strong className="text-primary-500">Good For:</strong>
              <ul className="text-sm text-white/60 mt-1 ml-4 list-disc">
                <li>Showing off your globe to others</li>
                <li>Passive viewing</li>
                <li>Getting a full 360° view</li>
              </ul>
            </div>
            <div>
              <strong className="text-primary-500">Turn Off For:</strong>
              <ul className="text-sm text-white/60 mt-1 ml-4 list-disc">
                <li>Adding flights</li>
                <li>Precise navigation</li>
                <li>Viewing specific routes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Understanding Flight Visualization</h3>
        <div className="glass rounded-lg p-4">
          <p className="text-white/70 mb-3">When you add a flight, it appears as:</p>
          <ul className="space-y-2 text-white/70 ml-4">
            <li><strong>Curved arc</strong> connecting departure and arrival airports</li>
            <li><strong>Great circle route</strong> - the shortest path on a sphere</li>
            <li><strong>Blue/cyan color</strong> that stands out against the globe</li>
            <li><strong>Raised height</strong> - arcs curve upward from the surface</li>
          </ul>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Navigation Tips</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Finding Flights</strong>
            <p className="text-sm text-white/60 mt-1">Zoom into the region and rotate to the optimal angle</p>
          </div>
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Best Viewing</strong>
            <p className="text-sm text-white/60 mt-1">Center the ocean between continents for transatlantic/pacific routes</p>
          </div>
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Mobile Tip</strong>
            <p className="text-sm text-white/60 mt-1">Rotate to landscape mode for a better view</p>
          </div>
          <div className="glass rounded-lg p-3">
            <strong className="text-primary-500">Performance</strong>
            <p className="text-sm text-white/60 mt-1">Disable auto-rotate if performance is slow</p>
          </div>
        </div>
      </section>

      <section className="glass rounded-lg p-4 bg-primary-500/10">
        <h3 className="text-lg font-semibold mb-2">💡 Globe Tips</h3>
        <ul className="space-y-2 text-white/70 text-sm">
          <li>✅ There's no "wrong way" to navigate - explore freely!</li>
          <li>✅ Different zoom levels reveal different details</li>
          <li>✅ Try viewing routes from different angles</li>
          <li>✅ More flights create a more impressive visualization</li>
        </ul>
      </section>
    </div>
  );

  const renderManagingFriends = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold gradient-text">Managing Friends</h2>

      <section>
        <h3 className="text-xl font-semibold mb-3">Finding and Adding Friends</h3>
        <ol className="space-y-3 text-white/70 ml-4 list-decimal">
          <li>
            <strong className="text-white">Open Friends Panel</strong>
            <p className="text-sm mt-1">Click the "Friends" button in the top navigation bar.</p>
          </li>
          <li>
            <strong className="text-white">Search for Users</strong>
            <p className="text-sm mt-1">Type a friend's name or email in the search box. Search is case-insensitive.</p>
          </li>
          <li>
            <strong className="text-white">Send Friend Request</strong>
            <p className="text-sm mt-1">Click "Add Friend" next to the person you want to connect with.</p>
          </li>
          <li>
            <strong className="text-white">Wait for Approval</strong>
            <p className="text-sm mt-1">Your request appears in their Pending Requests tab.</p>
          </li>
        </ol>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Managing Requests</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">Pending Requests</strong>
            <p className="text-sm text-white/60 mt-2">Friend requests you've received from others. Accept or decline.</p>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">Sent Requests</strong>
            <p className="text-sm text-white/60 mt-2">Requests you've sent waiting for approval. Can cancel anytime.</p>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">Friends</strong>
            <p className="text-sm text-white/60 mt-2">Accepted connections you can interact with.</p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Interacting with Friends</h3>
        <div className="space-y-3">
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">Compare Stats</strong>
            <p className="text-white/70 text-sm mt-2">
              Click "Compare Stats" to see a side-by-side comparison of your travel statistics including flights, countries, distance, and common airports.
            </p>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">View Globe</strong>
            <p className="text-white/70 text-sm mt-2">
              Click "View Globe" to see their flight routes on the 3D globe. The header shows "Viewing [Friend]'s Globe". Click the back arrow to return to your view.
            </p>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">Remove Friend</strong>
            <p className="text-white/70 text-sm mt-2">
              Click "Remove" to disconnect. They immediately lose access to your globe and stats.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Privacy and Permissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="glass rounded-lg p-4">
            <strong className="text-green-500">✅ What Friends Can See</strong>
            <ul className="text-sm text-white/60 mt-2 ml-4 list-disc space-y-1">
              <li>Your display name and profile picture</li>
              <li>Your flight statistics</li>
              <li>Your complete globe with all flights</li>
              <li>Your flight list (airports, cities, dates)</li>
            </ul>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-red-500">❌ What Friends Cannot See</strong>
            <ul className="text-sm text-white/60 mt-2 ml-4 list-disc space-y-1">
              <li>Your email address</li>
              <li>Cannot edit or delete your flights</li>
              <li>Cannot access account settings</li>
              <li>Cannot add flights for you</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="glass rounded-lg p-4 bg-primary-500/10">
        <h3 className="text-lg font-semibold mb-2">💡 Friend Tips</h3>
        <ul className="space-y-2 text-white/70 text-sm">
          <li>✅ Only accept requests from people you know and trust</li>
          <li>✅ Set up your profile before adding friends so they recognize you</li>
          <li>✅ Check Friends panel regularly for pending requests (watch for the notification badge)</li>
          <li>✅ Use Compare Stats to motivate more travel!</li>
          <li>✅ Viewing friends' globes is great for travel inspiration</li>
        </ul>
      </section>
    </div>
  );

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold gradient-text">Profile Settings</h2>

      <section>
        <h3 className="text-xl font-semibold mb-3">Setting Your Display Name</h3>
        <div className="glass rounded-lg p-4">
          <p className="text-white/70 mb-3">Your display name is how you appear to other users.</p>
          <ol className="space-y-2 text-white/70 ml-4 list-decimal">
            <li>Click your profile icon in the top right corner</li>
            <li>Find the "Display Name" field</li>
            <li>Enter your preferred name (max 50 characters)</li>
            <li>Click "Save Changes"</li>
            <li>Your name updates immediately for all users</li>
          </ol>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Adding a Profile Picture</h3>
        <div className="glass rounded-lg p-4">
          <p className="text-white/70 mb-3">Your profile picture appears in the header and friend lists.</p>
          <ol className="space-y-2 text-white/70 ml-4 list-decimal">
            <li>Open Profile Settings (click your profile icon)</li>
            <li>Find the "Profile Picture URL" field</li>
            <li>Paste a direct link to an image file (must be publicly accessible)</li>
            <li>Supported formats: JPG, PNG, GIF</li>
            <li>Click "Save Changes"</li>
          </ol>
          <div className="mt-4 p-3 bg-white/5 rounded-lg">
            <strong className="text-primary-500 text-sm">Image URL Requirements:</strong>
            <ul className="text-xs text-white/60 mt-2 ml-4 list-disc space-y-1">
              <li>Must start with http:// or https://</li>
              <li>Direct link to image file (ends in .jpg, .png, .gif)</li>
              <li>Example: https://example.com/mypicture.jpg</li>
              <li>Square images work best (e.g., 200x200 pixels)</li>
            </ul>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Privacy Controls</h3>
        <div className="glass rounded-lg p-4">
          <h4 className="font-semibold mb-2">Who Can See Your Profile?</h4>
          <ul className="space-y-2 text-white/70 text-sm ml-4 list-disc">
            <li><strong>Display Name & Picture:</strong> Visible in search results to all users</li>
            <li><strong>Flight Data & Statistics:</strong> Only visible to accepted friends</li>
            <li><strong>Email Address:</strong> Never visible to other users</li>
          </ul>
          
          <h4 className="font-semibold mb-2 mt-4">Controlling Access</h4>
          <p className="text-white/70 text-sm">
            Only accept friend requests from people you know. Remove friends anytime to revoke their access to your data.
          </p>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Account Management</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">Changing Password</strong>
            <p className="text-sm text-white/60 mt-2">
              Sign out and click "Forgot Password" on the login screen. Follow the email reset instructions.
            </p>
          </div>
          <div className="glass rounded-lg p-4">
            <strong className="text-primary-500">Google SSO</strong>
            <p className="text-sm text-white/60 mt-2">
              If you signed in with Google, your Google name and picture are used initially but can be customized.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-semibold mb-3">Session Management</h3>
        <div className="glass rounded-lg p-4">
          <p className="text-white/70 mb-3">Your session persists until you sign out or it expires from inactivity.</p>
          <div className="p-3 bg-white/5 rounded-lg">
            <strong className="text-primary-500 text-sm">Signing Out:</strong>
            <p className="text-white/60 text-sm mt-1">
              Click "Sign Out" in the top right. Your data is saved and secure. Sign back in anytime to access your globe.
            </p>
          </div>
        </div>
      </section>

      <section className="glass rounded-lg p-4 bg-primary-500/10">
        <h3 className="text-lg font-semibold mb-2">💡 Profile Tips</h3>
        <ul className="space-y-2 text-white/70 text-sm">
          <li>✅ Set your display name right after creating your account</li>
          <li>✅ Use a name and picture that friends will recognize</li>
          <li>✅ Don't include sensitive information in your display name</li>
          <li>✅ Square profile pictures work best (200x200 recommended)</li>
          <li>✅ Test your profile by having a friend search for you</li>
        </ul>
      </section>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'home':
        return renderHome();
      case 'getting-started':
        return renderGettingStarted();
      case 'adding-flights':
        return renderAddingFlights();
      case 'using-globe':
        return renderUsingGlobe();
      case 'managing-friends':
        return renderManagingFriends();
      case 'profile-settings':
        return renderProfileSettings();
      default:
        return renderHome();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            {activeSection !== 'home' && (
              <button
                onClick={() => setActiveSection('home')}
                className="btn-ghost p-2"
                title="Back to guide menu"
                aria-label="Back to guide menu"
              >
                ←
              </button>
            )}
            <Book className="w-6 h-6 text-primary-500" aria-hidden="true" />
            <h2 className="text-xl font-bold gradient-text">User Guides</h2>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost p-2"
            title="Close help"
            aria-label="Close help"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto p-6 help-scroll"
          role="region"
          aria-label="Help content"
          tabIndex={0}
        >
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 text-center text-sm text-white/50">
          Questions? Click the Help button anytime to access these guides
        </div>
      </div>
    </div>
  );
}
